// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Dates lues dans l'API et dates saisies dans les formulaires.
 *
 * Le backend rend parfois un horodatage UTC sans suffixe de fuseau (datetime
 * naïf sorti de la base). Lu tel quel, le navigateur le prendrait pour une heure
 * locale : un serveur vu il y a une minute paraîtrait hors ligne depuis deux
 * heures en été, et un ordre de clé émis il y a dix minutes franchirait à tort
 * le seuil de souffrance. `dateUtc` est la lecture commune de ces horodatages.
 */

/** Date d'un horodatage de l'API, lu en UTC quand il ne dit pas son fuseau.
 *  Rend null pour une valeur absente ou illisible. */
export function dateUtc(iso) {
  if (!iso) return null;
  const withTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(iso);
  const d = new Date(withTimezone ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date et heure locales d'un horodatage de l'API. `fallback` est le texte
 *  affiché quand l'horodatage manque ou ne se lit pas : chaque écran garde le
 *  sien (« jamais », un tiret). */
export function dateTime(iso, fallback) {
  const d = dateUtc(iso);
  return d ? d.toLocaleString('fr-FR') : fallback;
}

export function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
}

/** Valeur d'un champ `type="date"` tirée d'une date ISO. */
export function dateForField(iso) {
  return iso && iso.length >= 10 ? iso.slice(0, 10) : '';
}

/** Une date saisie au jour (champ `type="date"`) vers le datetime qu'attend
 *  l'API. Minuit UTC, écrit explicitement : le backend lit un datetime sans
 *  fuseau comme UTC, mais le dire ici évite qu'un navigateur réglé sur un autre
 *  fuseau décale la recette ou la dénonciation d'un jour. */
export function dayToIso(day) {
  return day ? `${day}T00:00:00+00:00` : null;
}

/** Date du jour au format d'un champ `type="date"`. */
export function today() {
  return new Date().toISOString().slice(0, 10);
}
